"use client";

import Link from "next/link";
import clsx from "clsx";
import { useNavbar } from "@/shared/components/Navbar/useNavbar";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { config } from "@/shared/utils/config";
import { ArrowRight } from "lucide-react";

interface NavbarProps {
  scrollThreshold?: number;
}

export default function Navbar({ scrollThreshold = 100 }: Readonly<NavbarProps>) {
  const { isScrolled, heroRef } = useNavbar({
    threshold: scrollThreshold,
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const firstMobileLinkRef = useRef<HTMLAnchorElement | null>(null);

  function handleToggleMenu() {
    setIsMenuOpen(function toggle(prev) {
      return !prev;
    });
  }

  function handleCloseMenu() {
    setIsMenuOpen(false);
  }

  function handleOverlayClick() {
    setIsMenuOpen(false);
  }

  useEffect(function setupKeyHandlers() {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return function cleanup() {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(function closeOnDesktopResize() {
    function onResize() {
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false);
      }
    }
    window.addEventListener("resize", onResize);
    return function cleanup() {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <nav className="sticky top-3 z-50 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] lg:fixed lg:inset-x-0">
      <div
        className={clsx(
          "mx-auto px-4 sm:px-6 lg:px-8 transition-[max-width,padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
          isScrolled ? "max-w-4xl" : "max-w-4xl lg:max-w-[75rem]"
        )}
      >
        <div
          className={clsx(
            "relative isolate my-3 flex h-14 items-center justify-between overflow-hidden rounded-2xl [&>*]:relative [&>*]:z-10",
            "transition-[height,padding,background-color,border-color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
            "backdrop-blur-2xl backdrop-saturate-200",
            "before:pointer-events-none before:absolute before:inset-0 before:z-0 before:rounded-[inherit] before:bg-[linear-gradient(135deg,rgba(255,255,255,0.58)_0%,rgba(255,255,255,0.22)_38%,rgba(37,99,235,0.08)_100%)]",
            "after:pointer-events-none after:absolute after:inset-x-4 after:top-0 after:h-px after:bg-white/75",
            isScrolled
              ? "border border-white/35 bg-white/24 px-4 ring-1 ring-white/55 dark:border-white/15 dark:bg-white/10 dark:ring-white/20 sm:px-5 lg:h-14 lg:rounded-full [box-shadow:0_1px_0_rgba(255,255,255,.72)_inset,0_-1px_0_rgba(255,255,255,.24)_inset,0_18px_45px_rgba(15,23,42,.10)]"
              : "border border-white/45 bg-white/28 px-4 ring-1 ring-white/60 dark:border-white/15 dark:bg-white/10 dark:ring-white/20 sm:px-5 lg:rounded-none lg:border-transparent lg:bg-transparent lg:ring-0 lg:backdrop-blur-none lg:before:opacity-0 lg:after:opacity-0 lg:[box-shadow:none] [box-shadow:0_1px_0_rgba(255,255,255,.78)_inset,0_-1px_0_rgba(255,255,255,.26)_inset,0_18px_45px_rgba(15,23,42,.09)]"
            // isScrolled
            //   ? "backdrop-blur-xs bg-white/30 dark:bg-white/10 backdrop-saturate-150 ring-1 ring-white/70 dark:ring-white/25 border border-white/20 [box-shadow:0_0_0_1px_rgba(255,255,255,.65)_inset,0_10px_28px_rgba(0,0,0,.07)]"
            //   : "bg-transparent ring-0 border-transparent shadow-none"
          )}
        >
          {/* Logo */}
          <Link
            href="/"
            className={clsx(
              "flex items-center transition-[gap] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
              isScrolled ? "gap-2" : "gap-2 md:gap-3"
            )}
          >
            <div className="shrink-0 flex items-center justify-center rounded-sms overflow-hidden">
              <Image
                src="/branding/logo.png"
                alt="Lucien AI logo"
                width={36}
                height={36}
                className={clsx(
                  "h-7 w-7 transition-[width,height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                  !isScrolled && "lg:h-9 lg:w-9"
                )}
              />
            </div>
            <span
              className={clsx(
                "font-sg text-lg font-semibold text-foreground transition-[font-size] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                !isScrolled && "lg:text-2xl"
              )}
            >
              Lucien AI
            </span>
          </Link>

          {/* Navigation Links */}
          <div
            className={clsx(
              "hidden items-center transition-[gap] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] lg:flex",
              isScrolled ? "gap-8" : "gap-10"
            )}
          >
            <Link
              href={"/#deliverables"}
              className="text-foreground/90 transition-colors hover:text-foreground"
            >
              Outcomes
            </Link>
            <Link
              href={"/#sprint"}
              className="text-foreground/90 transition-colors hover:text-foreground"
            >
              How It Works
            </Link>
            <Link
              href={"/#integrations"}
              className="text-foreground/90 transition-colors hover:text-foreground"
            >
              Integrations
            </Link>
            <Link
              href={"/#proof"}
              className="text-foreground/90 transition-colors hover:text-foreground"
            >
              Proof
            </Link>
            <Link
              href="/pricing"
              className="text-foreground/90 transition-colors hover:text-foreground"
            >
              Pricing
            </Link>
            {/* <Link
              href="/about"
              className="text-foreground/80 transition-colors hover:text-foreground"
            >
              About
            </Link> */}
            {/* <LiquidPopover
              ariaLabel="Open solutions menu"
              align="left"
              offsetY={20}
              applySlightTranslucency={isScrolled}
              trigger={<span className="inline-flex items-center">Solutions</span>}
              triggerClassName="text-foreground/80 hover:text-foreground !px-0"
              openOnHover
              groupId="navbar"
            >
              <div className="w-full">
                <div className="px-2 pb-2">
                  <div className="text-xs font-semibold text-foreground/70 px-2 py-1">
                    Solutions
                  </div>
                  <div className="grid grid-cols-1 gap-1">
                    <Link
                      href="/solutions/engineering"
                      className="group flex items-start gap-3 rounded-lg px-3 py-2 text-sm hover:bg-white/40 dark:hover:bg-white/10 transition-colors"
                    >
                      <span className="mt-0.5 h-2 w-2 rounded-full bg-blue/70" aria-hidden="true" />
                      <span className="flex-1">
                        <span className="block font-semibold text-foreground">Engineering</span>
                        <span className="block text-xs text-foreground/70">
                          Ship 10× faster without headcount risk
                        </span>
                      </span>
                    </Link>
                    <Link
                      href="/solutions/product"
                      className="group flex items-start gap-3 rounded-lg px-3 py-2 text-sm hover:bg-white/40 dark:hover:bg-white/10 transition-colors"
                    >
                      <span className="mt-0.5 h-2 w-2 rounded-full bg-blue/70" aria-hidden="true" />
                      <span className="flex-1">
                        <span className="block font-semibold text-foreground">Product</span>
                        <span className="block text-xs text-foreground/70">
                          Turn specs into shipped product
                        </span>
                      </span>
                    </Link>
                    <Link
                      href="/solutions/support"
                      className="group flex items-start gap-3 rounded-lg px-3 py-2 text-sm hover:bg-white/40 dark:hover:bg-white/10 transition-colors"
                    >
                      <span className="mt-0.5 h-2 w-2 rounded-full bg-blue/70" aria-hidden="true" />
                      <span className="flex-1">
                        <span className="block font-semibold text-foreground">Support</span>
                        <span className="block text-xs text-foreground/70">
                          Resolve issues while you sleep
                        </span>
                      </span>
                    </Link>
                    <Link
                      href="/solutions/operations"
                      className="group flex items-start gap-3 rounded-lg px-3 py-2 text-sm hover:bg-white/40 dark:hover:bg-white/10 transition-colors"
                    >
                      <span className="mt-0.5 h-2 w-2 rounded-full bg-blue/70" aria-hidden="true" />
                      <span className="flex-1">
                        <span className="block font-semibold text-foreground">Operations</span>
                        <span className="block text-xs text-foreground/70">
                          Automate the heavy lifting
                        </span>
                      </span>
                    </Link>
                  </div>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2 rounded-xl border border-white/20 bg-white/10 p-2 text-xs backdrop-blur dark:border-white/10 dark:bg-white/5">
                  <span className="text-foreground/75">Not sure where to start?</span>
                  <Link
                    href="/demo"
                    className="rounded-lg bg-blue px-3 py-1 font-semibold text-white transition-colors hover:brightness-110"
                    data-cta="solutions-menu-demo"
                  >
                    Book a demo
                  </Link>
                </div>
              </div>
            </LiquidPopover>
            <LiquidPopover
              ariaLabel="Open resources menu"
              align="left"
              offsetY={20}
              applySlightTranslucency={isScrolled}
              trigger={<span className="inline-flex items-center">Resources</span>}
              triggerClassName="text-foreground/80 hover:text-foreground !px-0"
              openOnHover
              groupId="navbar"
            >
              <div className="w-full">
                <div className="px-2 pb-2">
                  <div className="text-xs font-semibold text-foreground/70 px-2 py-1">
                    Resources
                  </div>
                  <div className="grid grid-cols-1 gap-1">
                    <a
                      href="/help"
                      className="group flex items-start gap-3 rounded-lg px-3 py-2 text-sm hover:bg-white/40 dark:hover:bg-white/10 transition-colors"
                    >
                      <span className="mt-0.5 h-2 w-2 rounded-full bg-blue/70" aria-hidden="true" />
                      <span className="flex-1">
                        <span className="block font-semibold text-foreground">Docs</span>
                        <span className="block text-xs text-foreground/70">
                          Product guide &mdash; onboarding, how-to&apos;s
                        </span>
                      </span>
                    </a>
                    <Link
                      href="/blog"
                      className="group flex items-start gap-3 rounded-lg px-3 py-2 text-sm hover:bg-white/40 dark:hover:bg-white/10 transition-colors"
                    >
                      <span className="mt-0.5 h-2 w-2 rounded-full bg-blue/70" aria-hidden="true" />
                      <span className="flex-1">
                        <span className="block font-semibold text-foreground">Blog</span>
                        <span className="block text-xs text-foreground/70">
                          Latest news, tutorials, and updates
                        </span>
                      </span>
                    </Link>
                    <Link
                      href="/legal"
                      className="group flex items-start gap-3 rounded-lg px-3 py-2 text-sm hover:bg-white/40 dark:hover:bg-white/10 transition-colors"
                    >
                      <span className="mt-0.5 h-2 w-2 rounded-full bg-blue/70" aria-hidden="true" />
                      <span className="flex-1">
                        <span className="block font-semibold text-foreground">Legal</span>
                        <span className="block text-xs text-foreground/70">
                          Terms, privacy, and policies
                        </span>
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </LiquidPopover> */}
          </div>

          {/* CTA Buttons (desktop) */}
          <div className="hidden items-center gap-3 lg:flex">
            {/* <Link
              href="/login"
              className="px-3 py-2 text-sm font-medium text-foreground/80 hover:text-foreground"
            >
              Login
            </Link> */}
            <div className="overflow-hidden rounded-full p-1">
              <Link
                href="/sign-up"
                className={clsx(
                  "inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-blue px-4 text-sm font-semibold text-white shadow-sm transition-[filter] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:brightness-110 active:brightness-95",
                  "ring-4 ring-transparent hero-cta-animated [animation:hero-cta-ring_1.8s_ease-in-out_infinite_alternate]"
                )}
                data-cta="navbar-get-started"
              >
                Start free
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              type="button"
              className="p-2 rounded-lg transition-colors duration-200 text-foreground hover:bg-black/5"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              onClick={handleToggleMenu}
            >
              {isMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile full-screen menu overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-[60] lg:hidden"
          role="dialog"
          aria-modal="true"
          id="mobile-menu"
        >
          <div
            className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/50 backdrop-blur-sm"
            onClick={handleOverlayClick}
          />
          <div className="relative mx-4 mt-4 rounded-2xl overflow-y-auto overflow-x-hidden shadow-[0_10px_35px_-10px_rgba(0,0,0,0.45)] ring-1 ring-white/60 dark:ring-white/15 backdrop-blur-xl bg-white/30 dark:bg-white/10 text-black dark:text-white transition-all duration-300 ease-out translate-y-0 opacity-100">
            <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-blue/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -left-24 h-72 w-72 rounded-full bg-white/25 dark:bg-white/10 blur-3xl" />

            <div className="relative flex items-center justify-between p-4">
              <Link href="/" className="flex items-center gap-2" onClick={handleCloseMenu}>
                <div className="shrink-0 flex items-center justify-center rounded-sms overflow-hidden">
                  <Image
                    src="/branding/logo.png"
                    alt="Lucien AI logo"
                    width={30}
                    height={30}
                    style={{ width: "auto", height: "auto" }}
                  />
                </div>
                <span className="text-lg font-semibold">Lucien AI</span>
              </Link>
              <button
                type="button"
                className="p-2 rounded-lg transition-colors duration-200 text-foreground hover:bg-white/40 hover:backdrop-blur-md"
                aria-label="Close menu"
                onClick={handleCloseMenu}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="relative px-4">
              <div className="mb-3 rounded-xl px-3 py-3 ring-1 ring-white/60 dark:ring-white/15 bg-white/35 dark:bg-white/10 backdrop-blur-lg">
                <p className="text-sm leading-snug text-foreground/90">
                  AI-assisted GTM execution for early founders.
                </p>
              </div>
            </div>
            <div className="relative px-2 pb-4">
              <nav className="flex flex-col">
                {/* Outcomes */}
                <Link
                  href={"/#mobile-outcomes"}
                  ref={firstMobileLinkRef}
                  onClick={handleCloseMenu}
                  className="px-3 py-3 rounded-xl text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10 hover:backdrop-blur-md transition-colors ring-1 ring-transparent hover:ring-white/60 dark:hover:ring-white/15"
                >
                  Outcomes
                </Link>
                {/* How It Works */}
                <Link
                  href={"/#mobile-work"}
                  onClick={handleCloseMenu}
                  className="mt-2 px-3 py-3 rounded-xl text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10 hover:backdrop-blur-md transition-colors ring-1 ring-transparent hover:ring-white/60 dark:hover:ring-white/15"
                >
                  How It Works
                </Link>
                {/* Integrations */}
                <Link
                  href={"/#mobile-integrations"}
                  onClick={handleCloseMenu}
                  className="mt-2 px-3 py-3 rounded-xl text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10 hover:backdrop-blur-md transition-colors ring-1 ring-transparent hover:ring-white/60 dark:hover:ring-white/15"
                >
                  Integrations
                </Link>
                {/* Proof */}
                <Link
                  href={"/#mobile-proof"}
                  onClick={handleCloseMenu}
                  className="mt-2 px-3 py-3 rounded-xl text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10 hover:backdrop-blur-md transition-colors ring-1 ring-transparent hover:ring-white/60 dark:hover:ring-white/15"
                >
                  Proof
                </Link>
                <Link
                  href="/pricing"
                  onClick={handleCloseMenu}
                  className="mt-2 px-3 py-3 rounded-xl text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10 hover:backdrop-blur-md transition-colors ring-1 ring-transparent hover:ring-white/60 dark:hover:ring-white/15"
                >
                  Pricing
                </Link>
                {/* About (commented to match desktop) */}
                {/**
                <Link
                  href="/about"
                  onClick={handleCloseMenu}
                  className="mt-2 px-3 py-3 rounded-xl text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10 hover:backdrop-blur-md transition-colors ring-1 ring-transparent hover:ring-white/60 dark:hover:ring-white/15"
                >
                  About
                </Link>
                */}
                {/* Solutions */}
                {/**
                <div className="mt-2">
                  <button
                    type="button"
                    className="w-full px-3 py-3 rounded-xl text-left text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10 hover:backdrop-blur-md transition-colors ring-1 ring-transparent hover:ring-white/60 dark:hover:ring-white/15 inline-flex items-center justify-between"
                    aria-expanded={isSolutionsOpen}
                    aria-controls="mobile-solutions"
                    onClick={function toggleSolutions() {
                      setIsSolutionsOpen(function update(prev) {
                        return !prev;
                      });
                    }}
                  >
                    <span>Solutions</span>
                    <svg
                      className={clsx(
                        "h-5 w-5 transition-transform",
                        isSolutionsOpen ? "rotate-180" : "rotate-0"
                      )}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <div
                    id="mobile-solutions"
                    role="region"
                    className={clsx(
                      "overflow-hidden transition-all",
                      isSolutionsOpen ? "max-h-80" : "max-h-0"
                    )}
                  >
                    <div className="flex flex-col gap-1 px-2 py-2">
                      <Link href="/solutions/engineering" onClick={handleCloseMenu} className="px-3 py-2 rounded-lg text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10">Engineering</Link>
                      <Link href="/solutions/product" onClick={handleCloseMenu} className="px-3 py-2 rounded-lg text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10">Product</Link>
                      <Link href="/solutions/support" onClick={handleCloseMenu} className="px-3 py-2 rounded-lg text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10">Support</Link>
                      <Link href="/solutions/operations" onClick={handleCloseMenu} className="px-3 py-2 rounded-lg text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10">Operations</Link>
                    </div>
                  </div>
                </div>
                */}
                {/* Resources */}
                {/**
                <div className="mt-2">
                  <button
                    type="button"
                    className="w-full px-3 py-3 rounded-xl text-left text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10 hover:backdrop-blur-md transition-colors ring-1 ring-transparent hover:ring-white/60 dark:hover:ring-white/15 inline-flex items-center justify-between"
                    aria-expanded={isResourcesOpen}
                    aria-controls="mobile-resources"
                    onClick={function toggleResources() {
                      setIsResourcesOpen(function update(prev) {
                        return !prev;
                      });
                    }}
                  >
                    <span>Resources</span>
                    <svg
                      className={clsx(
                        "h-5 w-5 transition-transform",
                        isResourcesOpen ? "rotate-180" : "rotate-0"
                      )}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <div
                    id="mobile-resources"
                    role="region"
                    className={clsx(
                      "overflow-hidden transition-all",
                      isResourcesOpen ? "max-h-80" : "max-h-0"
                    )}
                  >
                    <div className="flex flex-col gap-1 px-2 py-2">
                      <a href="/help" onClick={handleCloseMenu} className="px-3 py-2 rounded-lg text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10">Docs</a>
                      <Link href="/blog" onClick={handleCloseMenu} className="px-3 py-2 rounded-lg text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10">Blog</Link>
                      <Link href="/blog" onClick={handleCloseMenu} className="px-3 py-2 rounded-lg text-foreground/90 hover:bg-white/45 dark:hover:bg-white/10">Legal</Link>
                    </div>
                  </div>
                </div>
                */}
              </nav>
              <div className="mt-4 flex flex-col gap-2">
                <Link
                  href="/sign-up"
                  onClick={handleCloseMenu}
                  className="w-full inline-flex h-11 items-center justify-center rounded-xl bg-blue/90 px-4 text-sm font-semibold text-white shadow-[0_10px_25px_-10px_rgba(0,71,249,0.8)] transition-colors hover:brightness-110 active:brightness-95"
                  data-cta="mobile-sign-up"
                >
                  Start free
                </Link>
                <Link
                  href={config.cal.url}
                  target="_blank"
                  onClick={handleCloseMenu}
                  className="w-full inline-flex h-10 items-center justify-center rounded-xl ring-1 ring-white/50 dark:ring-white/15 px-4 text-sm font-semibold text-foreground bg-white/25 dark:bg-white/5 hover:bg-white/40 hover:backdrop-blur-md transition-colors"
                >
                  {/* Login */}
                  Talk through my GTM
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
